import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  useNegotiationDetail,
  useAdjustPrice,
  useApproveQuote,
  useRejectQuote,
  useSendReminder,
  QUOTE_STATUS_CONFIG,
} from '../../api/sales'
import {
  ArrowLeft,
  Send,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Package,
  Wrench,
  Building2,
  Phone,
  User,
  MessageSquare,
  Bell,
  ChevronDown,
  ChevronUp,
  Edit3,
  Plus,
  Trash2,
} from 'lucide-react'

export default function NegotiationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const quotationId = Number(id)
  const { data: negotiation, isLoading } = useNegotiationDetail(quotationId)
  const adjustPriceMutation = useAdjustPrice()
  const approveMutation = useApproveQuote()
  const rejectMutation = useRejectQuote()
  const sendReminderMutation = useSendReminder()

  const [showPriceAdjust, setShowPriceAdjust] = useState(false)
  const [adjustRemark, setAdjustRemark] = useState('')
  const [adjustItems, setAdjustItems] = useState<Array<{ itemName: string; specification?: string; quantity: number; unitPrice: number }>>([])
  const [newItem, setNewItem] = useState({ itemName: '', specification: '', quantity: 1, unitPrice: 0 })
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [expandedPrices, setExpandedPrices] = useState<Set<number>>(new Set())

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      negotiating: '洽谈中',
      awaiting_confirm: '待确认',
      confirmed: '已确认',
      rejected: '已拒绝',
      cancelled: '已作废',
      draft: '草稿',
    }
    return statusMap[status] || status
  }

  const togglePriceExpansion = (msgId: number) => {
    setExpandedPrices((prev) => {
      const next = new Set(prev)
      if (next.has(msgId)) {
        next.delete(msgId)
      } else {
        next.add(msgId)
      }
      return next
    })
  }

  const handleAddItem = () => {
    if (newItem.itemName && newItem.unitPrice > 0) {
      setAdjustItems([...adjustItems, { ...newItem }])
      setNewItem({ itemName: '', specification: '', quantity: 1, unitPrice: 0 })
    }
  }

  const handleRemoveItem = (index: number) => {
    setAdjustItems(adjustItems.filter((_, i) => i !== index))
  }

  const handleAdjustPrice = () => {
    if (adjustRemark.trim()) {
      adjustPriceMutation.mutate(
        { quotationId, data: { remark: adjustRemark, items: adjustItems } },
        {
          onSuccess: () => {
            setShowPriceAdjust(false)
            setAdjustRemark('')
            setAdjustItems([])
          },
        }
      )
    }
  }

  const handleApprove = () => {
    if (confirm('确认客户已接受此报价？')) {
      approveMutation.mutate({ quotationId })
    }
  }

  const handleReject = () => {
    if (rejectReason.trim()) {
      rejectMutation.mutate(
        { quotationId, reason: rejectReason },
        {
          onSuccess: () => {
            setShowRejectModal(false)
            setRejectReason('')
          },
        }
      )
    }
  }

  const handleSendReminder = () => {
    sendReminderMutation.mutate({ quotationId })
  }

  const getMessageIcon = (messageType: string) => {
    switch (messageType) {
      case 'QUOTE_SENT':
        return <Send className="w-4 h-4 text-blue-500" />
      case 'CUSTOMER_RESPONSE':
        return <MessageSquare className="w-4 h-4 text-green-500" />
      case 'PRICE_ADJUSTED':
        return <DollarSign className="w-4 h-4 text-amber-500" />
      case 'INTERNAL_NOTE':
        return <User className="w-4 h-4 text-gray-500" />
      case 'REMINDER_SENT':
        return <Bell className="w-4 h-4 text-purple-500" />
      default:
        return <MessageSquare className="w-4 h-4 text-gray-400" />
    }
  }

  const getMessageTypeLabel = (messageType: string): string => {
    const typeMap: Record<string, string> = {
      QUOTE_SENT: '报价已发送',
      CUSTOMER_RESPONSE: '客户回复',
      PRICE_ADJUSTED: '价格调整',
      INTERNAL_NOTE: '内部备注',
      REMINDER_SENT: '已发送提醒',
    }
    return typeMap[messageType] || messageType
  }

  const getSenderLabel = (sender: string): string => {
    const senderMap: Record<string, string> = {
      SALES: '销售人员',
      CUSTOMER: '客户',
      SYSTEM: '系统',
    }
    return senderMap[sender] || sender
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">加载中...</div>
        </div>
      </div>
    )
  }

  if (!negotiation) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">洽谈记录不存在</div>
        </div>
      </div>
    )
  }

  const canAdjust = negotiation.status === 'negotiating' || negotiation.status === 'awaiting_confirm'

  return (
    <div className="p-6">
      {/* 页面头部 */}
      <div className="mb-6">
        <Link
          to="/sales/negotiations"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          返回洽谈记录
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">
                {negotiation.quoteNo}
              </h1>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium"
                style={{
                  color: QUOTE_STATUS_CONFIG[negotiation.status as keyof typeof QUOTE_STATUS_CONFIG]?.color,
                  backgroundColor: QUOTE_STATUS_CONFIG[negotiation.status as keyof typeof QUOTE_STATUS_CONFIG]?.bgColor,
                }}
              >
                {negotiation.status === 'negotiating' && <Clock className="w-4 h-4" />}
                {negotiation.status === 'confirmed' && <CheckCircle className="w-4 h-4" />}
                {negotiation.status === 'rejected' && <XCircle className="w-4 h-4" />}
                {getStatusLabel(negotiation.status)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">工单号: {negotiation.workOrderNo}</p>
          </div>
          <div className="flex gap-2">
            {negotiation.status === 'negotiating' && (
              <>
                <button
                  onClick={() => setShowPriceAdjust(!showPriceAdjust)}
                  className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
                >
                  <Edit3 className="w-4 h-4" />
                  调整报价
                </button>
                <button
                  onClick={handleSendReminder}
                  disabled={sendReminderMutation.isPending}
                  className="px-4 py-2 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition flex items-center gap-2"
                >
                  <Bell className="w-4 h-4" />
                  发送提醒
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  确认成交
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  拒绝报价
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* 左侧主要内容 */}
        <div className="col-span-2 space-y-6">
          {/* 价格调整面板 */}
          {showPriceAdjust && canAdjust && (
            <div className="bg-white rounded-xl border border-blue-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900">调整报价</h2>
                <button
                  onClick={() => setShowPriceAdjust(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* 调整项列表 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  报价明细
                </label>
                <div className="space-y-2 mb-3">
                  {adjustItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="text"
                        value={item.itemName}
                        onChange={(e) => {
                          const newItems = [...adjustItems]
                          newItems[index].itemName = e.target.value
                          setAdjustItems(newItems)
                        }}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                        placeholder="项目名称"
                      />
                      <input
                        type="text"
                        value={item.specification || ''}
                        onChange={(e) => {
                          const newItems = [...adjustItems]
                          newItems[index].specification = e.target.value
                          setAdjustItems(newItems)
                        }}
                        className="w-24 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                        placeholder="规格"
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...adjustItems]
                          newItems[index].quantity = Number(e.target.value)
                          setAdjustItems(newItems)
                        }}
                        className="w-20 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                        placeholder="数量"
                      />
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => {
                          const newItems = [...adjustItems]
                          newItems[index].unitPrice = Number(e.target.value)
                          setAdjustItems(newItems)
                        }}
                        className="w-28 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                        placeholder="单价"
                      />
                      <span className="text-sm text-gray-600">
                        ¥{((item.quantity || 0) * (item.unitPrice || 0)).toLocaleString()}
                      </span>
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* 添加新项 */}
                <div className="flex items-center gap-2 p-3 border border-dashed border-gray-300 rounded-lg">
                  <input
                    type="text"
                    value={newItem.itemName}
                    onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })}
                    className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                    placeholder="项目名称"
                  />
                  <input
                    type="text"
                    value={newItem.specification}
                    onChange={(e) => setNewItem({ ...newItem, specification: e.target.value })}
                    className="w-24 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                    placeholder="规格"
                  />
                  <input
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: Number(e.target.value) })}
                    className="w-20 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                    placeholder="数量"
                  />
                  <input
                    type="number"
                    value={newItem.unitPrice || ''}
                    onChange={(e) => setNewItem({ ...newItem, unitPrice: Number(e.target.value) })}
                    className="w-28 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                    placeholder="单价"
                  />
                  <button
                    onClick={handleAddItem}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* 调整说明 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  调整说明
                </label>
                <textarea
                  value={adjustRemark}
                  onChange={(e) => setAdjustRemark(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="请说明调整原因..."
                />
              </div>

              {/* 总计 */}
              {adjustItems.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">调整后总价</span>
                    <span className="text-lg font-semibold text-blue-600">
                      ¥{adjustItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleAdjustPrice}
                  disabled={!adjustRemark.trim() || adjustPriceMutation.isPending}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                >
                  提交调整
                </button>
                <button
                  onClick={() => setShowPriceAdjust(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  取消
                </button>
              </div>
            </div>
          )}

          {/* 洽谈消息记录 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">洽谈记录</h2>
            </div>
            <div className="p-6 space-y-4">
              {negotiation.messages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p>暂无洽谈记录</p>
                </div>
              ) : (
                negotiation.messages.map((message) => (
                  <div
                    key={message.id}
                    className={`border rounded-lg p-4 ${
                      message.sender === 'CUSTOMER'
                        ? 'border-blue-200 bg-blue-50/50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getMessageIcon(message.messageType)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {message.senderName || getSenderLabel(message.sender)}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(message.createdAt).toLocaleString('zh-CN')}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                            {getMessageTypeLabel(message.messageType)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{message.content}</p>

                        {/* 价格调整详情 */}
                        {message.messageType === 'PRICE_ADJUSTED' && message.newPrice && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="flex items-center justify-between cursor-pointer" onClick={() => togglePriceExpansion(message.id)}>
                              <span className="text-sm font-medium text-amber-800">调整后价格</span>
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-amber-600">
                                  ¥{message.newPrice.toLocaleString()}
                                </span>
                                {expandedPrices.has(message.id) ? (
                                  <ChevronUp className="w-4 h-4 text-amber-600" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-amber-600" />
                                )}
                              </div>
                            </div>
                            {message.discountAmount && message.discountAmount > 0 && (
                              <div className="text-sm text-amber-700">
                                优惠金额: ¥{message.discountAmount.toLocaleString()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* 右侧信息栏 */}
        <div className="space-y-6">
          {/* 基本信息 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">基本信息</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">医院</div>
                  <div className="text-sm font-medium text-gray-900">
                    {negotiation.hospitalName}
                  </div>
                </div>
              </div>
              {negotiation.contactName && (
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">联系人</div>
                    <div className="text-sm font-medium text-gray-900">
                      {negotiation.contactName}
                    </div>
                  </div>
                </div>
              )}
              {negotiation.contactPhone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">联系电话</div>
                    <div className="text-sm text-gray-900">
                      {negotiation.contactPhone}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">预计工期</div>
                  <div className="text-sm font-medium text-gray-900">
                    {negotiation.estimatedDuration}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 价格信息 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">当前报价</h2>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">配件费用</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  ¥{negotiation.partsTotal.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">服务费用</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  ¥{negotiation.serviceTotal.toLocaleString()}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">报价总额</span>
                  <span className="text-xl font-bold text-primary-600">
                    ¥{negotiation.currentPrice.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 快捷操作 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">快捷操作</h2>
            </div>
            <div className="p-4 space-y-2">
              <Link
                to={`/sales/quotes/${quotationId}`}
                className="block w-full px-4 py-2 text-center text-blue-600 hover:bg-blue-50 rounded-lg transition"
              >
                查看报价详情
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 拒绝报价弹窗 */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-6 h-6 text-red-500" />
              <h2 className="text-lg font-semibold">拒绝报价</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              请输入拒绝原因，此信息将记录在洽谈记录中。
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
              placeholder="请说明拒绝原因..."
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                取消
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-50"
              >
                确认拒绝
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
